import { useState, useEffect, useCallback } from "react";
import { Button, Input, Select, Space, message, DatePicker, Tag } from "antd";
import { ReloadOutlined, DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import DataTable from "../../components/common/DataTable";
import {
  listApproverLog,
  exportApproverLog,
} from "../../services/cashCod.service";

const { RangePicker } = DatePicker;

const ACTION_COLORS = {
  Approved: { color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
  Rejected: { color: "#b91c1c", bg: "#fef2f2", border: "#fecaca" },
  Flagged: { color: "#c2410c", bg: "#fff7ed", border: "#fed7aa" },
};

const ACTION_OPTIONS = [
  { label: "Approved", value: "Approved" },
  { label: "Rejected", value: "Rejected" },
];

function ActionBadge({ action }) {
  const style = ACTION_COLORS[action] || {
    color: "#475569",
    bg: "#f8fafc",
    border: "#e2e8f0",
  };
  return (
    <Tag
      style={{
        color: style.color,
        background: style.bg,
        borderColor: style.border,
        fontWeight: 600,
      }}
    >
      {action || "—"}
    </Tag>
  );
}

function formatDateTime(value) {
  if (!value) return "—";
  return dayjs(value).format("YYYY-MM-DD HH:mm");
}

function formatOmr(value) {
  if (value == null || value === "") return "—";
  return Number(value).toLocaleString("en-US", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
}

function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * COD Approver Log — read-only audit trail of approve/reject actions.
 */
export default function ApproverLogTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [action, setAction] = useState(undefined);
  const [dateRange, setDateRange] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: pageSize,
        search: search || undefined,
        action: action || undefined,
      };
      if (dateRange?.[0] && dateRange?.[1]) {
        params.fromDate = dateRange[0].startOf("day").toISOString();
        params.toDate = dateRange[1].endOf("day").toISOString();
      }

      const response = await listApproverLog(params);
      setRows(response?.data?.records || []);
      setTotalCount(response?.data?.pagination?.total_count || 0);
    } catch {
      message.error("Failed to load approver log");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, action, dateRange]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleExport = async () => {
    try {
      const params = {
        search: search || undefined,
        action: action || undefined,
      };
      if (dateRange?.[0] && dateRange?.[1]) {
        params.fromDate = dateRange[0].startOf("day").toISOString();
        params.toDate = dateRange[1].endOf("day").toISOString();
      }
      const blob = await exportApproverLog(params);
      downloadBlob(blob, `COD_Approver_Log_${dayjs().format("YYYY-MM-DD")}.csv`);
    } catch {
      message.error("Export failed");
    }
  };

  const columns = [
    {
      title: "Approver",
      dataIndex: "approver",
      key: "approver",
      render: (v) => v || "—",
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: 120,
      render: (v) => <ActionBadge action={v} />,
    },
    {
      title: "Date/Time",
      dataIndex: "dateTime",
      key: "dateTime",
      width: 160,
      render: formatDateTime,
    },
    {
      title: "Rider",
      key: "rider",
      render: (_, r) => {
        if (!r.partnerId && !r.rider) return "—";
        if (r.partnerId && r.rider) return `${r.partnerId} — ${r.rider}`;
        return r.rider || r.partnerId || "—";
      },
    },
    {
      title: "COD Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      align: "right",
      render: formatOmr,
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      ellipsis: true,
      render: (v) => v || "—",
    },
  ];

  return (
    <div>
      <Space wrap style={{ marginBottom: 16, width: "100%", justifyContent: "space-between" }}>
        <Space wrap>
          <Input.Search
            allowClear
            placeholder="Search approver or rider"
            style={{ width: 240 }}
            onSearch={(v) => {
              setPage(1);
              setSearch(v);
            }}
          />
          <Select
            allowClear
            placeholder="Action"
            style={{ width: 140 }}
            options={ACTION_OPTIONS}
            value={action}
            onChange={(v) => {
              setPage(1);
              setAction(v);
            }}
          />
          <RangePicker
            value={dateRange}
            onChange={(v) => {
              setPage(1);
              setDateRange(v);
            }}
          />
          <Button icon={<ReloadOutlined />} onClick={fetchList}>
            Refresh
          </Button>
        </Space>
        <Button icon={<DownloadOutlined />} onClick={handleExport}>
          Export CSV
        </Button>
      </Space>

      <DataTable
        rowKey="_id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{ current: page, pageSize, total: totalCount }}
        onPageChange={(p) => setPage(p)}
        scroll={{ x: 1000 }}
      />
    </div>
  );
}
